import { useCallback, useEffect } from 'react';
import { loadPartialScene } from '@inter-ikea-storageone/range/src/utils/sceneUtils';
import reportError from 'error-reporting/reportError';
import { plannerRenderSettings } from 'config/plannerRenderSettings';
import { useServiceSettings } from 'contexts/serviceSettings';
import { isKiosk, isMobile, isProduction } from 'utils/platform';
import { getKvadratFeatures } from 'utils/queryUtils';
import { usePlannerConfig } from 'contexts/plannerConfig';
import { isSpaceEnabled } from 'utils/integration';
import useResetPlanner from './useResetPlanner';
import { useCore } from 'contexts/core';

export type PostMessageEvent = {
  data: PostMessagePayload;
};

export type PostMessagePayload = {
  action: PostMessageAction;
} & PostMessageData;

export type PostMessageAction =
  | 'prepareConfigurator'
  | 'onConfiguratorReady'
  | 'onOpenConfigurator'
  | 'onConfigurationDone'
  | 'onStartConfigurator';

export type PostMessageData = {
  payload: {
    roomVpc?: string;
    serializedEntities?: string;
  };
};

const useSpacePostMessage = (canvas: React.RefObject<HTMLCanvasElement>) => {
  const coreApi = useCore();
  const resetPlanner = useResetPlanner(false);

  const {
    dexfEndpoints,
    kompisSettings: { applicationName, locale, dexfApiKey },
    dexfSettings: {
      localisation: { useMetricMeasures }
    }
  } = useServiceSettings();

  const { plannerConfig } = usePlannerConfig();

  const enableSpacePlanning = isSpaceEnabled();

  const onPostMessage = useCallback(
    async (e: PostMessageEvent) => {
      if (e.data.action === 'onOpenConfigurator') {
        resetPlanner();

        const serializedEntities = e.data.payload.serializedEntities;
        const kvadratVpc = e.data.payload.roomVpc;
        const room = coreApi.kvadratRoom?.getBlueprint();

        if (!kvadratVpc || !canvas.current) return;

        if (!room) {
          coreApi.kvadratRoom?.createRoom(
            coreApi.ipexViewer,
            plannerRenderSettings,
            canvas.current,
            applicationName,
            locale,
            dexfEndpoints.baseUrl,
            dexfApiKey,
            isProduction() ? 'prod' : 'qa',
            coreApi.core,
            useMetricMeasures,
            isKiosk(),
            getKvadratFeatures(),
            plannerConfig.roomOptions,
            kvadratVpc
          );
        } else {
          coreApi.kvadratRoom?.loadBlueprint(kvadratVpc);
        }

        coreApi.lights?.setupEnvironmentmapValues();

        coreApi.kvadratRoom?.loadBlueprint(kvadratVpc).then(async () => {
          if (!serializedEntities) return;

          try {
            await loadPartialScene(
              serializedEntities,
              false,
              coreApi.getServices(),
              coreApi.kvadratRoom,
              coreApi.placementService,
              kvadratVpc
            );
          } catch (e) {
            reportError(e, {
              serializedEntities
            });
          }

          const centerPosition = coreApi.kvadratRoom?.getRoomCenterPosition();
          if (centerPosition) {
            coreApi.lights?.centerLights(centerPosition);
          }

          coreApi.camera.focusSolutionAfterLoad(isMobile(), true);
        });
      }
    },
    [
      useMetricMeasures,
      canvas,
      applicationName,
      coreApi,
      dexfApiKey,
      dexfEndpoints.baseUrl,
      locale,
      plannerConfig.roomOptions,
      resetPlanner
    ]
  );

  useEffect(() => {
    if (enableSpacePlanning) {
      window.addEventListener('message', onPostMessage);
    }

    return () => {
      if (enableSpacePlanning) {
        window.removeEventListener('message', onPostMessage);
      }
    };
  }, [coreApi, enableSpacePlanning, onPostMessage]);
};

export default useSpacePostMessage;
